import React, {
  forwardRef,
  useContext,
  useEffect,
  useImperativeHandle,
  useState,
  useCallback,
} from "react";
import EditIcon from "@mui/icons-material/Edit";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { toast, ToastContainer } from "react-toastify";

import { AuthContext } from "../../../context/auth-context";
import Modal from "../../../Component/Modal/Modal";
import ConfirmModal from "../../../Component/ConfirmModal";
import Table, {
  StyledTableCell,
  StyledTableRow,
} from "../../../Component/UI/Table/Table";
import MUIIconButton from "../../../Component/UI/MUIIconButton/MUIIconButton";
import Spinner from "../../../Component/Spinner/Spinner";
import EmptyView from "../../../Component/EmptyView/EmptyView";
import ConversationMonitoringForm from "./ConversationMonitoringForm";
import brandClasses from "../Brand/Brand.module.css";
import {
  fetchConversationMonitoringConfigs,
  createConversationMonitoringConfig,
  updateConversationMonitoringConfig,
  deleteConversationMonitoringConfig,
} from "../../../services/conversationMonitoringService";

const TABLE_HEADERS = [
  { id: "category", name: "Category", sorting: false },
  { id: "entity", name: "Brand / Entity", sorting: false },
  { id: "persona", name: "Persona", sorting: false },
  { id: "type", name: "Type", sorting: false },
  { id: "query_type", name: "Query Type", sorting: false },
  { id: "status", name: "Status", sorting: false },
  { id: "date_added", name: "Date Added", sorting: false },
  { id: "actions", name: "Actions", sorting: false },
];

const DEFAULT_FORM_VALUES = {
  customer_id: "",
  category: "",
  entity: "",
  persona: "",
  type: "brand",
  query_type: "brand",
  exclude_brand_owned_sources: false,
  site_suggestions: [],
};

function formatDate(isoString) {
  if (!isoString) return "—";
  const date = new Date(isoString);
  const formattedDate = date.toLocaleDateString("en-GB");
  const formattedTime = date.toLocaleTimeString("en-US", {
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  });
  return `${formattedDate} ${formattedTime}`;
}

// forwardRef + useImperativeHandle: Settings.js's shared top-right "Add X" header
// button lives outside this component (above the tab bar, matching Brand/Product/etc.),
// so it needs a way to trigger our create form without lifting all our modal state up.
const ConversationMonitoring = forwardRef(function ConversationMonitoring(_props, ref) {
  const authCtx = useContext(AuthContext);
  // Every role (Super Admin, Super User, Company admin) is scoped to their own
  // customer — no customer picker anywhere in this tab. customer_id comes from the
  // same globally-synced AuthContext value every other Settings tab already uses.
  const ownCustomerId = authCtx?.user?.customer_id || null;
  const ownCustomerName = authCtx?.user?.customer?.name || "";

  const [customers, setCustomers] = useState([]);
  const [configs, setConfigs] = useState([]);
  const [configsLoading, setConfigsLoading] = useState(false);

  const [isFormOpen, setIsFormOpen] = useState(false);
  const [isUpdateMode, setIsUpdateMode] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formValues, setFormValues] = useState(DEFAULT_FORM_VALUES);

  const [deleteTarget, setDeleteTarget] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  useEffect(() => {
    // The form's read-only Customer display (update mode) looks up the name from this list.
    if (ownCustomerId) {
      setCustomers([{ id: ownCustomerId, name: ownCustomerName }]);
    }
  }, [ownCustomerId, ownCustomerName]);

  const loadConfigs = useCallback(() => {
    if (!ownCustomerId) return;
    setConfigsLoading(true);
    fetchConversationMonitoringConfigs(ownCustomerId)
      .then(setConfigs)
      .catch(() => toast.error("Failed to load conversation monitoring configs"))
      .finally(() => setConfigsLoading(false));
  }, [ownCustomerId]);

  useEffect(() => {
    loadConfigs();
  }, [loadConfigs]);

  const handleOpenCreate = () => {
    setIsUpdateMode(false);
    setEditingId(null);
    setFormValues({ ...DEFAULT_FORM_VALUES, customer_id: ownCustomerId });
    setIsFormOpen(true);
  };

  useImperativeHandle(ref, () => ({ openCreate: handleOpenCreate }));

  const handleOpenEdit = (item) => {
    setIsUpdateMode(true);
    setEditingId(item.id);
    setFormValues({
      customer_id: item.customer_id,
      category: item.category,
      entity: item.entity,
      persona: item.persona || "",
      type: item.type,
      query_type: item.query_type,
      exclude_brand_owned_sources: !!item.exclude_brand_owned_sources,
      site_suggestions: Array.isArray(item.site_suggestions) ? item.site_suggestions : [],
    });
    setIsFormOpen(true);
  };

  const handleCloseForm = () => {
    setIsFormOpen(false);
    setFormValues(DEFAULT_FORM_VALUES);
    setEditingId(null);
  };

  const handleSubmitForm = async (values) => {
    try {
      if (isUpdateMode) {
        await updateConversationMonitoringConfig(editingId, values);
        toast.success("Conversation monitoring config updated");
      } else {
        if (!values.customer_id) {
          toast.error("Unable to determine your customer — please refresh and try again.");
          return;
        }
        await createConversationMonitoringConfig(values);
        toast.success("Conversation monitoring config created");
      }
      handleCloseForm();
      loadConfigs();
    } catch (error) {
      toast.error(error?.response?.data?.message || "Something went wrong");
    }
  };

  const handleDeleteClick = (item) => {
    setDeleteTarget(item);
    setShowDeleteConfirm(true);
  };

  const handleCancelDelete = () => {
    setShowDeleteConfirm(false);
    setDeleteTarget(null);
  };

  const handleConfirmDelete = async () => {
    if (!deleteTarget) return;
    try {
      await deleteConversationMonitoringConfig(deleteTarget.id, deleteTarget.customer_id);
      toast.success("Conversation monitoring config deleted");
      loadConfigs();
    } catch (error) {
      toast.error(error?.response?.data?.message || "Failed to delete config");
    } finally {
      setShowDeleteConfirm(false);
      setDeleteTarget(null);
    }
  };

  const renderRow = (item) => (
    <StyledTableRow key={item.id}>
      <StyledTableCell style={{ width: 160 }}>{item.category}</StyledTableCell>
      <StyledTableCell style={{ width: 140 }}>{item.entity}</StyledTableCell>
      <StyledTableCell style={{ width: 180 }}>{item.persona || "—"}</StyledTableCell>
      <StyledTableCell style={{ width: 90 }}>{item.type}</StyledTableCell>
      <StyledTableCell style={{ width: 110 }}>{item.query_type}</StyledTableCell>
      <StyledTableCell style={{ width: 120 }}>
        <div className={brandClasses.status}>
          <CheckCircleIcon style={{ color: item.is_active ? "#028101" : "#bdbdbd" }} />
          <span>{item.is_active ? "Active" : "Inactive"}</span>
        </div>
      </StyledTableCell>
      <StyledTableCell style={{ width: 180 }}>{formatDate(item.date_added)}</StyledTableCell>
      <StyledTableCell align="left">
        <div>
          <MUIIconButton
            tooltip="Update"
            style={{ color: "#007aff" }}
            className={brandClasses.action_btn}
            onClick={() => handleOpenEdit(item)}
          >
            <EditIcon />
          </MUIIconButton>
          <MUIIconButton
            tooltip="Delete"
            style={{ color: "#da1a04" }}
            className={brandClasses.action_btn}
            onClick={() => handleDeleteClick(item)}
          >
            <DeleteOutlineIcon />
          </MUIIconButton>
        </div>
      </StyledTableCell>
    </StyledTableRow>
  );

  return (
    <>
      {configsLoading ? (
        <Spinner />
      ) : configs.length !== 0 ? (
        <Table headers={TABLE_HEADERS} data={configs} render={renderRow} />
      ) : (
        <EmptyView message="No conversation monitoring configs for this customer yet." />
      )}

      <Modal
        title={isUpdateMode ? "Update Conversation Monitoring" : "Add Conversation Monitoring"}
        open={isFormOpen}
        handleClose={handleCloseForm}
      >
        <ConversationMonitoringForm
          customers={customers}
          formValues={formValues}
          setFormValues={setFormValues}
          isUpdateMode={isUpdateMode}
          allowCustomerSelection={false}
          handleCloseModal={handleCloseForm}
          onSubmit={handleSubmitForm}
        />
      </Modal>

      <ConfirmModal
        show={showDeleteConfirm}
        handleClose={handleCancelDelete}
        handleConfirmDeletion={handleConfirmDelete}
        selectedId={deleteTarget?.id}
        title="Delete Conversation Monitoring Config?"
        message={
          deleteTarget
            ? `Are you sure you want to delete the config for "${deleteTarget.entity}" (${deleteTarget.category}${
                deleteTarget.persona ? `, ${deleteTarget.persona}` : ""
              })?`
            : undefined
        }
      />

      <ToastContainer />
    </>
  );
});

export default ConversationMonitoring;
